import {
  PLOT_COUNT,
  START_DAY_TIME,
  STARTING_COINS,
  STARTING_PLOTS,
  STARTING_SEEDS,
  ORDER_INTERVAL,
} from './constants';
import type { FlowerId } from './flowers';
import { rollMarket } from './market';
import { generateOrder } from './orders';
import { Rng } from './rng';
import { UPGRADE_IDS, type UpgradeId } from './upgrades';

export const STATE_VERSION = 1;

export interface Crop {
  species: FlowerId;
  /** 0 = just planted, 1 = in full bloom. */
  growth: number;
  /** Golden blooms are decided when the flower opens and sell for much more. */
  golden: boolean;
}

export interface Plot {
  id: number;
  unlocked: boolean;
  /** 0 = bone dry, 1 = freshly watered. Decays over MOISTURE_DURATION seconds. */
  moisture: number;
  crop: Crop | null;
}

export interface FlowerStock {
  /** Normal flowers. */
  n: number;
  /** Golden flowers. */
  g: number;
}

export interface OrderItem {
  species: FlowerId;
  qty: number;
}

export interface Order {
  id: number;
  /** Index into CUSTOMERS. */
  customer: number;
  items: OrderItem[];
  reward: number;
  xp: number;
}

export interface MarketState {
  day: number;
  multipliers: Record<FlowerId, number>;
  featured: FlowerId;
}

export interface WeatherState {
  raining: boolean;
  /** Seconds of rain left while raining. */
  timeLeft: number;
  /** Absolute simulation time the next shower starts, if one is scheduled. */
  nextRainAt: number | null;
  rainbowLeft: number;
}

export interface AlmanacEntry {
  harvested: number;
  golden: number;
}

export interface GameStats {
  planted: number;
  harvested: number;
  coinsEarned: number;
  ordersCompleted: number;
}

/**
 * The complete, serializable state of one farm. This is what gets saved to
 * localStorage and what a multiplayer host streams to its visitors.
 */
export interface GameState {
  version: number;
  farmName: string;
  rngState: number;
  /** Total simulated seconds. */
  time: number;
  day: number;
  /** Fraction of the current day: 0 = midnight, 0.5 = noon. */
  dayTime: number;
  coins: number;
  xp: number;
  plots: Plot[];
  seeds: Partial<Record<FlowerId, number>>;
  flowers: Partial<Record<FlowerId, FlowerStock>>;
  upgrades: Record<UpgradeId, number>;
  almanac: Partial<Record<FlowerId, AlmanacEntry>>;
  market: MarketState;
  weather: WeatherState;
  orders: Order[];
  nextOrderId: number;
  orderTimer: number;
  sprinklerTimer: number;
  stats: GameStats;
}

export function createInitialState(seed: number = Date.now()): GameState {
  const plots: Plot[] = Array.from({ length: PLOT_COUNT }, (_, id) => ({
    id,
    unlocked: STARTING_PLOTS.includes(id),
    moisture: 0,
    crop: null,
  }));

  const upgrades = Object.fromEntries(UPGRADE_IDS.map((id) => [id, 0])) as Record<
    UpgradeId,
    number
  >;

  const state: GameState = {
    version: STATE_VERSION,
    farmName: 'Sunny Petal Farm',
    rngState: seed >>> 0,
    time: 0,
    day: 1,
    dayTime: START_DAY_TIME,
    coins: STARTING_COINS,
    xp: 0,
    plots,
    seeds: { ...STARTING_SEEDS },
    flowers: {},
    upgrades,
    almanac: {},
    // Placeholder replaced below once the RNG can run against the state.
    market: { day: 1, multipliers: {} as Record<FlowerId, number>, featured: 'daisy' },
    weather: { raining: false, timeLeft: 0, nextRainAt: null, rainbowLeft: 0 },
    orders: [],
    nextOrderId: 1,
    orderTimer: ORDER_INTERVAL,
    sprinklerTimer: 0,
    stats: { planted: 0, harvested: 0, coinsEarned: 0, ordersCompleted: 0 },
  };

  const rng = new Rng(state);
  state.market = rollMarket(state, rng);
  state.orders.push(generateOrder(state, rng));
  return state;
}

export function seedCount(state: GameState, id: FlowerId): number {
  return state.seeds[id] ?? 0;
}

export function addSeeds(state: GameState, id: FlowerId, qty: number): void {
  state.seeds[id] = seedCount(state, id) + qty;
}

export function flowerStock(state: GameState, id: FlowerId): FlowerStock {
  return state.flowers[id] ?? { n: 0, g: 0 };
}

export function totalFlowers(state: GameState, id: FlowerId): number {
  const stock = flowerStock(state, id);
  return stock.n + stock.g;
}

export function isDiscovered(state: GameState, id: FlowerId): boolean {
  return (state.almanac[id]?.harvested ?? 0) > 0;
}
